import {
  Form,
  Input,
  InputNumber,
  Modal,
  DatePicker,
  Select,
  message,
} from 'antd'
import type { FormProps } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { patchCard } from '~/services/card'
import { ICard } from '~/types/card'

import { ModalFormContainer } from './style'

const ModalForm: React.FC<{
  open: boolean
  data: Partial<ICard>
  onClose: () => void
  refreshTable: () => void
}> = ({ open, data, onClose, refreshTable }) => {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const initialValues = useMemo(
    () => ({
      ...data,
      expiredTime: data.expiredTime ? dayjs(data.expiredTime) : undefined,
    }),
    [data],
  )

  const usedCount = Math.max(
    (data.totalCount ?? 0) - (data.count ?? 0),
    0,
  )

  const onFinish = useCallback<
    Required<FormProps<Partial<ICard>>>['onFinish']
  >(
    (values) => {
      if (!data.id) return
      setSubmitting(true)
      patchCard(data.id, {
        expiredTime: values.expiredTime
          ? dayjs(values.expiredTime).valueOf()
          : undefined,
        // The backend keeps the used count unchanged and derives the new
        // remaining balance from this target total.
        count: values.totalCount,
        remark: values.remark,
        status: values.status,
      })
        .then(() => {
          message.success('更新成功')
          refreshTable()
          onClose()
        })
        .catch((err) => {
          message.error(err?.message || '更新失败，请稍后再试')
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
    [data.id, onClose, refreshTable],
  )

  const handleCancel = useCallback(() => {
    onClose()
    form.resetFields()
  }, [form, onClose])

  const handleOk = useCallback(() => {
    form.submit()
  }, [form])

  return (
    <ModalFormContainer>
      <Modal
        open={open}
        onCancel={handleCancel}
        onOk={handleOk}
        confirmLoading={submitting}
        destroyOnClose
        title="更新卡信息"
      >
        <Form
          form={form}
          name="card-update"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          style={{ maxWidth: 600 }}
          initialValues={initialValues}
          onFinish={onFinish}
        >
          <Form.Item label="过期时间" name="expiredTime">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="总课时"
            name="totalCount"
            extra={`已使用 ${usedCount} 课时，剩余课时将自动调整`}
            rules={[{ required: true, message: '请输入总课时' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={usedCount}
              precision={0}
            />
          </Form.Item>
          <Form.Item label="状态" name="status">
            <Select
              allowClear
              options={[
                { label: '有效', value: 'ENABLED' },
                { label: '无效', value: 'DISABLED' },
              ]}
              placeholder="请选择状态"
            />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </ModalFormContainer>
  )
}

export default ModalForm
